import { useRef, useState } from "react";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, uploadFile } from "@/lib/api";

const MAX_MB = 20;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt";

export const FileUpload = ({ files, onChange, max = 5, id = "file-upload", label = "Attachments" }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const handle = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    let current = [...files];
    for (const file of picked) {
      if (current.length >= max) {
        toast.error(`You can attach up to ${max} files.`);
        break;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${file.name} exceeds ${MAX_MB} MB.`);
        continue;
      }
      try {
        setProgress({ name: file.name, pct: 0 });
        const res = await uploadFile(file, (pct) => setProgress({ name: file.name, pct }));
        current = [...current, res];
        onChange(current);
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    setProgress(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] font-medium text-slate-700">{label}</p>
      <button
        type="button"
        data-testid={`${id}-trigger`}
        onClick={() => inputRef.current?.click()}
        disabled={!!progress}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/60 px-6 py-8 text-center transition-colors hover:border-[#C20500]/50 hover:bg-[#C20500]/5"
      >
        {progress ? <Loader2 className="h-6 w-6 animate-spin text-[#C20500]" /> : <Paperclip className="h-6 w-6 text-slate-500" />}
        <span className="text-sm font-medium text-slate-800">
          {progress ? `Uploading ${progress.name} — ${progress.pct}%` : "Click to attach BOQ, drawings, specs or photos"}
        </span>
        <span className="text-xs text-slate-500">PDF, images, Word, Excel, CSV · up to {MAX_MB} MB each · max {max} files</span>
      </button>
      <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={handle} data-testid={`${id}-input`} />
      {files.length > 0 && (
        <ul className="space-y-2" data-testid={`${id}-list`}>
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-slate-500" />
                <span className="truncate">{f.filename}</span>
                <span className="mono shrink-0 text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
              </span>
              <button
                type="button"
                aria-label="Remove file"
                data-testid={`${id}-remove-${f.id}`}
                onClick={() => onChange(files.filter((x) => x.id !== f.id))}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#C20500]"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
